import { create } from 'zustand'
import { components, operations } from '@/lib/types/api-types'
import { api, queryClient } from '@/lib/api-client.ts'
import { toast } from '@/lib/hooks/useToast.ts'
import i18n from '@/i18n.ts'
import {
  clearMercureToken,
  closeAllMercureConnections,
} from '@/lib/helpers/subscribeToMercureTopic'
import { pausePersist, resumePersist } from '@/lib/queryPersister'
import { clearVisitedPaths } from '@/lib/helpers/visitedPaths'
import { ensureMediaPrincipal, purgeAccountMedia } from '@/lib/media/mediaAccountBoundary'
import { unregisterPushDevice } from '@/lib/push/registerPush'
import type { UserLicenceT } from '@/lib/licence/licence.ts'
import type { VoiceAiStateT } from '@/lib/voice/consent/index.ts'
import { useAiSuggestionDecisionStore } from '@/lib/aiSuggestion/aiSuggestionDecisionStore.ts'
// `projectsCount` is added to `user:me` by `UserProjectsCountNormalizer`, which OpenAPI
// does not see, so the generated schema cannot carry it. Optional: a user cached by an
// older build has no such field. `licence` comes the same way from `UserLicenceNormalizer`.
// `activeProjectsCount` (the same count without archived projects) and `demoStageId` (the
// stage of a live, unarchived demo the user can open) come from the same normalizer (D7).
// `voiceAi` (per-user access to AI dictation plus the last consent row) is normalizer-added too.
export type User = components['schemas']['User-user.me'] & {
  projectsCount?: number
  activeProjectsCount?: number
  demoStageId?: number | null
  licence?: UserLicenceT | null
  voiceAi?: VoiceAiStateT | null
}
export type OrganizationMembership = components['schemas']['OrganizationMember.jsonld-user.me']
export type Organization = components['schemas']['Organization-organization.read']
export type OrganizationRole =
  operations['api_organization_roles_get_collection']['responses']['200']['content']['application/ld+json']
export type LoginData = {
  email: string
  password: string
}

type UserStoreState = {
  user: User | null
  setUser: (user: User) => void
  organizationRoles: OrganizationRole | null
  setOrganizationRoles: (roles: OrganizationRole) => void
  currentOrganizationMembership: OrganizationMembership | null
  setCurrentOrganizationMembership: (organizationMembership: OrganizationMembership) => void
  currentOrganization: Organization | null
  loading: boolean
  loginUser: (data: LoginData) => Promise<User | null>
  /**
   * Install a user the server has just signed in by other means than `/login` — the signup
   * confirmation answers with the new user and its remember-me cookie. Same cache, media and
   * membership handling as `loginUser`.
   */
  startSession: (user: User) => Promise<void>
  logoutUser: () => Promise<void>
  /**
   * `logoutUser` that resolves only after the server has dropped the session
   * (push device removed, then `/logout`) and every per-user field is cleared.
   * For a page that must not send its next request with the previous user's
   * cookie — the signup confirmation (docs/plans/rejestracja-z-linku.md, section 6).
   */
  logoutUserAndWait: () => Promise<void>
  setLoading: (loading: boolean) => void
  setCurrentOrganization: (organization: Organization) => void
}

export const useUserStore = create<UserStoreState>((set, get) => {
  const endSession = async ({ waitForServer }: { waitForServer: boolean }) => {
    // Capture the id BEFORE clearing the store: the push teardown records the
    // delete against its owner, and by the time it runs `user` is already null.
    const departingUserId = get().user?.id ?? null
    set({ user: null, loading: false })
    // Decision markers are keyed by suggestion, not by user: the next account on a shared device
    // would see this one's pending or failed decision on the same issue.
    useAiSuggestionDecisionStore.getState().reset()
    if (waitForServer) {
      set({
        currentOrganizationMembership: null,
        currentOrganization: null,
        organizationRoles: null,
      })
    }
    // Remove this browser's push device BEFORE the session is dropped (the
    // DELETE needs the cookie), then log out — so a shared machine never keeps
    // delivering the previous user's notifications. Both calls are best-effort
    // and their responses are unused: the Symfony firewall logout clears the
    // session then 302-redirects to `/`, which the API has no route for, so the
    // XHR follows the redirect straight into a 404. The trailing `.catch` keeps
    // that 404 (and any push-teardown failure) from surfacing as an unhandled
    // "Request failed with status code 404" in the error reporter (issue #261).
    // reason: 'logout' — on failure the marker is demoted to a pending cleanup
    // rather than retained, so a leftover can never read as the NEXT user's
    // consent on a shared device (the backend upserts by token and re-points the
    // row at whoever is authenticated).
    const serverLogout = unregisterPushDevice({ userId: departingUserId, reason: 'logout' })
      .finally(() => api.get('/logout'))
      .catch(() => {})
    // Settled, not succeeded: a failed `/logout` leaves the caller to find the
    // session still alive on its next request, which it must handle anyway.
    if (waitForServer) await serverLogout
    // Pause persistence to suppress dehydration events during clear().
    // Resume is fire-and-forget — the cache is empty so the flush is trivial.
    pausePersist()
    try {
      queryClient.clear()
    } finally {
      void resumePersist()
    }
    // Clear Mercure token and close all EventSources to prevent data leak
    clearMercureToken()
    closeAllMercureConnections()
    clearVisitedPaths()
    // Last, and awaited by the caller before it shows the login form: aborts the stage
    // downloads, moves the SW and page write generations on, then deletes image-cache,
    // media-uploads-cache and the native attachment cache. Bounded and never rejects; see
    // mediaAccountBoundary.ts.
    await purgeAccountMedia()
  }

  return {
    user: null,
    setUser: (user: User) => {
      set({ user })
      // Media cached for a different (or unknown) previous user is purged in the background —
      // for an unknown one, only once connectivity is confirmed; see mediaAccountBoundary.ts.
      // A no-op once the marker names this user.
      if (user?.id != null) void ensureMediaPrincipal(user.id)
    },
    organizationRoles: null,
    setOrganizationRoles: (roles: OrganizationRole) => {
      set({ organizationRoles: roles })
    },
    currentOrganizationMembership: null,
    setCurrentOrganizationMembership: (organizationMembership: OrganizationMembership) => {
      set({ currentOrganizationMembership: organizationMembership })
    },
    currentOrganization: null,
    loading: true,
    setLoading: (loading: boolean) => {
      set({ loading })
    },

    startSession: async (user: User) => {
      // A session can start without a logout before it; see endSession.
      useAiSuggestionDecisionStore.getState().reset()
      // Clear all cached data from the previous session to prevent data leakage
      // between users on shared devices. Uses clear() (synchronous, no refetch)
      // instead of invalidateQueries() which triggered a thundering herd —
      // refetching hundreds of prefetched queries simultaneously, causing
      // WKWebView OOM on iOS.
      // Pause persistence to prevent PersistQueryClientProvider from dehydrating
      // the shrinking cache on each individual query removal during clear().
      pausePersist()
      try {
        queryClient.clear()
      } finally {
        await resumePersist()
      }
      // Awaited here, unlike in setUser, for ordering: left to run in the background, the purge
      // could delete media the new user has started downloading. Not a cross-user exposure —
      // the new user's screens request only URLs their own API returned.
      if (user.id != null) await ensureMediaPrincipal(user.id)
      set({ user, loading: false })
      if (user.organizationMemberships) {
        const organizationMembership: OrganizationMembership | undefined =
          user.organizationMemberships.find(
            // @ts-expect-error @id is there
            memberShip => memberShip?.['@id'] === user.activeMembership,
          )
        if (organizationMembership) {
          set({ currentOrganizationMembership: organizationMembership })
        } else {
          set({ currentOrganizationMembership: user.organizationMemberships?.[0] })
        }
      }
    },

    loginUser: async (data: LoginData) => {
      const res = await api.post('/login', data)
      const user: User = res?.data

      if (user) {
        await get().startSession(user)
        return user
      }
      toast({
        variant: 'destructive',
        title: i18n.t('common.error'),
        // @ts-expect-error - axios types are not correct
        description: res?.response?.data?.error,
      })
      set({ loading: false })
      return null
    },

    setCurrentOrganization: (organization: Organization) => {
      set({ currentOrganization: organization })
    },
    logoutUser: () => endSession({ waitForServer: false }),
    logoutUserAndWait: () => endSession({ waitForServer: true }),
  }
})
